// Ubicación en el repo: src/components/HearingAidControlsModal.jsx
// Controles/calibraciones de una venta de audífono — mismo contenido que el
// panel "Controles / calibraciones" de renderAudifonos() y el modal
// addControlPrompt() del prototipo (línea ~3082-3273), pero en un modal
// aparte porque esta pantalla usa tabla en vez de tarjetas (ver
// HearingAids.jsx). Cada control agenda automáticamente una cita "Control
// audífono" en Agenda (trigger en base de datos, ver hearingaidControls.js).
import { useEffect, useState } from 'react';
import {
  listControlsForHearingAid,
  createControl,
  toggleControlDone,
  softDeleteControl,
} from '../lib/hearingaidControls';
import { hearingAidSummaryLine } from '../lib/hearingaids';
import { professionalsForModuleAndBranch } from '../lib/professionals';
import { formatPatientName, formatProfessionalName, formatDate, todayISO, addDaysISO } from '../lib/format';
import { WORK_START } from '../lib/dayGridLayout';
import Spinner from './Spinner';

function emptyForm() {
  return { date: addDaysISO(todayISO(), 90), time: WORK_START, notes: '', professionalId: '' };
}

export default function HearingAidControlsModal({ hearingaid, branchId, professionals, actorId, onClose, onChanged }) {
  const professionalOptions = professionalsForModuleAndBranch(professionals, 'ORL', branchId);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      setControls(await listControlsForHearingAid(hearingaid.id));
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los controles.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hearingaid.id]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleAddSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.date) {
      setError('Indica la fecha.');
      return;
    }
    setSaving(true);
    try {
      await createControl({ hearingAidId: hearingaid.id, ...form }, actorId);
      setAdding(false);
      setForm(emptyForm());
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'No se pudo agendar el control.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(control) {
    setBusyId(control.id);
    setError('');
    try {
      await toggleControlDone(control.id, !control.done);
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar el control.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(control) {
    const ok = window.confirm('¿Eliminar este control? También se elimina la cita asociada en Agenda.');
    if (!ok) return;
    setBusyId(control.id);
    setError('');
    try {
      await softDeleteControl(control.id, actorId);
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el control.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h2>Controles / calibraciones</h2>
        <p className="field-hint" style={{ marginTop: -6 }}>
          {formatPatientName(hearingaid.patients)} · {hearingAidSummaryLine(hearingaid)}
        </p>

        {error && <p className="auth-error">{error}</p>}

        <p className="field-hint" style={{ marginTop: -2, marginBottom: 8 }}>
          Marcar "hecho" acá o marcar "Atendido" en Agenda hace lo mismo — quedan sincronizados. Si Agenda
          marca "No se presentó" y el paciente llega atrasado, tilda "hecho" acá para pasarlo a Atendido.
        </p>

        {loading ? (
          <Spinner />
        ) : controls.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Sin controles registrados.</p>
        ) : (
          <div>
            {controls.map((c) => (
              <div className="apt-item" key={c.id}>
                <input
                  type="checkbox"
                  checked={c.done}
                  disabled={busyId === c.id}
                  onChange={() => handleToggle(c)}
                  style={{ width: 16, height: 16 }}
                />
                <div className="apt-info">
                  <div className="name" style={c.done ? { textDecoration: 'line-through', color: 'var(--text-muted)' } : undefined}>
                    {formatDate(c.date)}
                    {c.time ? ` · ${c.time}` : ''}
                    {c.status === 'No se presentó' && (
                      <span className="badge badge-danger" style={{ marginLeft: 8 }}>
                        No se presentó
                      </span>
                    )}
                  </div>
                  <div className="meta">
                    {c.notes || 'Control / calibración'} · {formatProfessionalName(c.professionals)}
                  </div>
                </div>
                <button
                  type="button"
                  className="link-button link-button--danger"
                  disabled={busyId === c.id}
                  onClick={() => handleDelete(c)}
                >
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          {adding ? (
            <form onSubmit={handleAddSubmit} className="patient-form">
              <div className="form-row">
                <label className="field">
                  <span>Fecha de control</span>
                  <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} />
                </label>
                <label className="field">
                  <span>Hora</span>
                  <input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} />
                </label>
              </div>
              <label className="field">
                <span>Profesional</span>
                <select value={form.professionalId} onChange={(e) => set('professionalId', e.target.value)}>
                  <option value="">— Sin asignar —</option>
                  {professionalOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {formatProfessionalName(p, { withRole: true })}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Notas</span>
                <textarea
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="Calibración, ajuste, revisión de molde…"
                />
              </label>
              <p className="field-hint" style={{ marginTop: -4 }}>
                Este control se agregará automáticamente a la Agenda en la fecha y hora indicadas.
              </p>
              <div className="modal-actions">
                <button type="button" className="button-secondary" onClick={() => setAdding(false)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="button-primary" disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          ) : (
            <button type="button" className="button-secondary" onClick={() => setAdding(true)}>
              + Agendar control
            </button>
          )}
        </div>

        <div className="modal-actions" style={{ marginTop: 16 }}>
          <button type="button" className="button-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
